import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Checkbox from "@material-ui/core/Checkbox";
import Menu from "@material-ui/core/Menu";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import SelectAllIcon from "@material-ui/icons/SelectAll";
import ClearIcon from "@material-ui/icons/Clear";


import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Chip, Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import ContactTagListModal from "../../components/ContactTagListModal";
import { BulkSelectionProvider, useBulkSelection } from "../../context/BulkSelection/BulkSelectionContext";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TAGS":
      if (action.isFirstPage) {
        return [...action.payload];
      }
      const newTags = action.payload.filter(
        (tag) => !state.find((existingTag) => existingTag.id === tag.id)
      );
      return [...state, ...newTags];
    case "UPDATE_TAGS":
      const tag = action.payload;
      const tagIndex = state.findIndex((s) => s.id === tag.id);

      if (tagIndex !== -1) {
        state[tagIndex] = tag;
        return [...state];
      } else {
        return [tag, ...state];
      }
    case "DELETE_TAGS":
      const tagId = action.payload;
      return state.filter((tag) => tag.id !== tagId);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 180,
    [theme.breakpoints.down('sm')]: {
      minWidth: 120,
      margin: theme.spacing(0.5),
    },
  },
  filtersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  mobileHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  mobileTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  mobileFiltersSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  accordionButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    padding: theme.spacing(1, 0),
    fontWeight: 500,
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1),
      transition: 'transform 0.3s ease',
    },
    '&.expanded .MuiButton-startIcon': {
      transform: 'rotate(180deg)',
    },
  },
  searchField: {
    minWidth: 200,
    [theme.breakpoints.down('sm')]: {
      minWidth: 150,
    },
  },
  dateFieldsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
}));

const TagsContent = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, socket } = useContext(AuthContext);

  // Contexto de seleção múltipla
  const {
    isSelectionMode,
    selectedTickets: selectedTags,
    selectTicket: selectTag,
    isTicketSelected: isTagSelected,
    enableSelectionMode,
    disableSelectionMode,
    clearSelection
  } = useBulkSelection();

  const [selectedTagContacts, setSelectedTagContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTagName, setSelectedTagName] = useState("");
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [filterUserId, setFilterUserId] = useState(user.profile === 'admin' ? 0 : user.id);
  const [users, setUsers] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDateFilters, setShowDateFilters] = useState(false);
  
  // Estados para ações em lote
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [classificationDialogOpen, setClassificationDialogOpen] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState("");
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [pressingTagId, setPressingTagId] = useState(null);
  
  const pageNumberRef = useRef(1);
  const loadingRef = useRef(false);


  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(data);
      } catch (err) {
        toastError(err);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchMoreTags = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/tags/", {
          params: {
            searchParam,
            pageNumber,
            kanban: 0,
            orderBy,
            limit: 100,  // Solicitar 100 tags por página
            filterUserId: filterUserId || null,
            startDate: startDate || null,
            endDate: endDate || null
          },
        });
        dispatch({ type: "LOAD_TAGS", payload: data.tags, isFirstPage: pageNumber === 1 });
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };

    if (pageNumber > 0) {
      fetchMoreTags();
    }
  }, [searchParam, pageNumber, orderBy, filterUserId, startDate, endDate]);

  useEffect(() => {
    // Reseta o estado de tags e a paginação quando a pesquisa, ordenação ou filtros mudam
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(false);
  }, [searchParam, orderBy, filterUserId, startDate, endDate]);

  useEffect(() => {
    const onCompanyTags = (data) => {
      if (data.action === "update" || data.action === "create") {
        if ((data?.tag?.userId !== user.id && user.profile === 'user') && (!data?.tag?.isGlobal && data?.action === "create")) return;
        if ((data?.tag?.userId !== user.id && user.profile === 'user') && (!data?.tag?.isGlobal && data?.action === "update")) {
          dispatch({ type: "DELETE_TAGS", payload: +data.tag.id });
          return;
        }
        
        // Atualizar a tag específica
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
        
        // Se a modal estiver aberta e for a tag atual, atualizar também os contatos
        if (contactModalOpen && selectedTagName?.id === data.tag.id) {
          setSelectedTagContacts(data.tag.contacts || []);
        }
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onCompanyTags);

    return () => {
      socket.off(`company${user.companyId}-tag`, onCompanyTags);
    };
  }, [socket, user.companyId, contactModalOpen, selectedTagName]);

  const handleOpenTagModal = () => {
    setSelectedTagName(null); // Limpar tag selecionada para criar nova
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setTagModalOpen(false);
    setSelectedTagName(null); // Limpar tag selecionada ao fechar
  };

  const handleSearch = (event) => {
    const newSearchParam = event.target.value.toLowerCase();
    setSearchParam(newSearchParam);
  };

  const handleOrderChange = (event) => {
    setOrderBy(event.target.value);
  };

  const handleFilterUserChange = (event) => {
    setFilterUserId(event.target.value);
  };

  const handleStartDateChange = (event) => {
    const selectedStartDate = event.target.value;
    
    // Se já existe uma data final, validar se não é menor que a nova data inicial
    if (endDate && selectedStartDate > endDate) {
      setEndDate(""); // Limpar data final se for inválida
      toast.warning("Data final foi limpa pois era menor que a nova data inicial");
    }
    
    setStartDate(selectedStartDate);
  };

  const handleEndDateChange = (event) => {
    const selectedEndDate = event.target.value;
    
    // Validar se a data final não é menor que a data inicial
    if (startDate && selectedEndDate < startDate) {
      toast.error("A data final não pode ser menor que a data inicial");
      return;
    }
    
    setEndDate(selectedEndDate);
  };

  const handleApplyFilters = () => {
    // Validar se as datas estão corretas
    if (startDate && endDate && startDate > endDate) {
      toast.error("A data inicial não pode ser maior que a data final");
      return;
    }
    
    // Aplicar filtros - resetar paginação
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(false);
    // Forçar nova requisição com os filtros
    setTimeout(() => {
      fetchMoreTags();
    }, 100);
  };

  // Função para buscar tags (extraída do useEffect)
  const fetchMoreTags = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/tags/", {
        params: {
          searchParam,
          pageNumber: 1, // Sempre buscar primeira página ao aplicar filtros
          kanban: 0,
          orderBy,
          limit: 100,
          filterUserId: filterUserId || null,
          startDate: startDate || null,
          endDate: endDate || null
        },
      });
      dispatch({ type: "LOAD_TAGS", payload: data.tags, isFirstPage: true });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleClearFilters = () => {
    setFilterUserId(user.profile === 'admin' ? 0 : user.id);
    setStartDate("");
    setEndDate("");
    setSearchParam("");
    setOrderBy("name");
    // Resetar paginação
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(false);
    // Forçar nova requisição sem filtros
    setTimeout(() => {
      fetchMoreTags();
    }, 100);
  };

  const handleEditTag = (tag) => {
    setSelectedTagName(tag);
    setTagModalOpen(true);
  };

  const handleShowContacts = (contacts, tag) => {
    setSelectedTagContacts(contacts);
    setContactModalOpen(true);
    setSelectedTagName(tag);
  };

  const handleTagUpdate = async (updatedTag) => {
    console.log("Recebendo atualização da tag:", updatedTag);

    try {
      // Forçar nova busca de todas as tags para garantir atualização com filtros aplicados
      const { data: allTags } = await api.get("/tags/", {
        params: { 
          searchParam, 
          pageNumber: 1, 
          kanban: 0, 
          orderBy, 
          limit: 100,
          filterUserId: filterUserId || null,
          startDate: startDate || null,
          endDate: endDate || null
        }
      });

      // Substituir completamente o estado
      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_TAGS", payload: allTags.tags, isFirstPage: true });
      setHasMore(allTags.hasMore);
      setPageNumber(1); // Garantir que a paginação seja resetada

      console.log("Estado completamente atualizado com dados mais recentes e filtros aplicados");
      
      // Se a modal estiver aberta, atualizar também os contatos da tag específica
      if (contactModalOpen && selectedTagName?.id === updatedTag.id) {
        try {
          const { data: tagData } = await api.get(`/tags/${updatedTag.id}`);
          if (tagData && tagData.contacts) {
            setSelectedTagContacts(tagData.contacts);
            console.log("Contatos da modal atualizados:", tagData.contacts);
          }
        } catch (modalError) {
          console.error("Erro ao atualizar contatos da modal:", modalError);
        }
      }
      
    } catch (error) {
      console.error("Erro ao buscar tag atualizada:", error);
      // Em caso de erro, recarregar a página
      toastError(error);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedTagContacts([]);
    setSelectedTagName("");
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    if (loading || !hasMore) return;
    
    if (loadingRef.current) return;
    loadingRef.current = true;
    
    setTimeout(() => {
      setPageNumber((prevPageNumber) => prevPageNumber + 1);
      loadingRef.current = false;
    }, 300);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMore();
    }
  };

  const toggleDateFilters = () => {
    setShowDateFilters(!showDateFilters);
  };

  // Funções para multi-select com long press
  const handleLongPress = () => {
    if (user.profile === "admin" && !isSelectionMode) {
      enableSelectionMode();
      // Não selecionar automaticamente a tag no long press
    }
  };

  // Para detectar long press (desktop e mobile)
  const longPressTimer = useRef(null);
  
  const startLongPress = () => {
    if (user.profile === "admin") {
      longPressTimer.current = setTimeout(handleLongPress, 500);
    }
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Eventos para desktop
  const handleMouseDown = () => {
    startLongPress();
  };

  const handleMouseUp = () => {
    cancelLongPress();
  };

  const handleMouseLeave = () => {
    cancelLongPress();
  };



  const handleSelectAll = () => {
    if (selectedTags.length === tags.length) {
      clearSelection();
    } else {
      // Selecionar todas as tags que não estão selecionadas
      tags.forEach(tag => {
        if (!isTagSelected(tag.id)) {
          selectTag(tag);
        }
      });
    }
  };

  const handleActionMenuOpen = (event) => {
    if (selectedTags.length > 0) {
      setActionMenuAnchor(event.currentTarget);
    }
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  const handleBulkDelete = () => {
    setActionMenuAnchor(null);
    setBulkDeleteConfirmOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      // Verificar se alguma tag tem contatos
      const tagsWithContacts = selectedTags.filter(tag => tag.contacts && tag.contacts.length > 0);
      
      if (tagsWithContacts.length > 0) {
        toast.error("Não é permitido excluir uma TAG com contatos atribuídos, substitua os contatos para outra TAG antes");
        setBulkDeleteConfirmOpen(false);
        return;
      }
      
      const tagIds = selectedTags.map(tag => tag.id);
      
      await api.delete('/tags/bulk', {
        data: { tagIds }
      });
      
      toast.success(`${selectedTags.length} tag(s) excluída(s) com sucesso!`);
      clearSelection();
      setBulkDeleteConfirmOpen(false);
      
      // Recarregar a lista
      dispatch({ type: "RESET" });
      setPageNumber(1);
      setHasMore(false);
      setTimeout(() => {
        fetchMoreTags();
      }, 100);
    } catch (err) {
      toastError(err);
    }
  };

  const handleBulkClassification = () => {
    setActionMenuAnchor(null);
    setClassificationDialogOpen(true);
  };

  const handleClassificationConfirm = async () => {
    try {
      const tagIds = selectedTags.map(tag => tag.id);
      
      await api.put('/tags/bulk/classification', {
        tagIds,
        class: selectedClassification
      });
      
      toast.success(`${selectedTags.length} tag(s) atualizada(s) com sucesso!`);
      clearSelection();
      setClassificationDialogOpen(false);
      setSelectedClassification("");
      
      // Recarregar a lista
      dispatch({ type: "RESET" });
      setPageNumber(1);
      setHasMore(false);
      setTimeout(() => {
        fetchMoreTags();
      }, 100);
    } catch (err) {
      toastError(err);
    }
  };

  const isAllSelected = () => {
    return tags.length > 0 && selectedTags.length === tags.length;
  };

  const isIndeterminate = () => {
    return selectedTags.length > 0 && selectedTags.length < tags.length;
  };





  return (
    <MainContainer className={classes.mainContainer}>
      {contactModalOpen && (
              <ContactTagListModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        tag={selectedTagName}
        onTagUpdate={handleTagUpdate}
      />
      )}
      <ConfirmationModal
        title={deletingTag && `${i18n.t("tags.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <TagModal
        open={tagModalOpen}
        onClose={handleCloseTagModal}
        aria-labelledby="form-dialog-title"
        tagId={selectedTagName?.id || null}
        kanban={0}
      />
      

      <MainHeader>
        {isMobile ? (
          // Layout Mobile - Título e filtros integrados
          <Box className={classes.mobileHeader}>
            <Box className={classes.mobileTitleRow}>
              <Title style={{ margin: 0 }}>{i18n.t("tags.title")} ({tags.length})</Title>
              {user.profile === 'admin' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenTagModal}
                  size="small"
                >
                  {i18n.t("tags.buttons.add")}
                </Button>
              )}
            </Box>

            <Box className={classes.mobileFiltersSection}>
              <TextField
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "gray" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl variant="outlined" fullWidth>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={orderBy}
                  onChange={handleOrderChange}
                  label="Ordenar por"
                >
                  <MenuItem value="name">Nome (A-Z)</MenuItem>
                  <MenuItem value="nameDesc">Nome (Z-A)</MenuItem>
                  <MenuItem value="id">ID (Menor-Maior)</MenuItem>
                  <MenuItem value="idDesc">ID (Maior-Menor)</MenuItem>
                  <MenuItem value="user">Dono (A-Z)</MenuItem>
                  <MenuItem value="userDesc">Dono (Z-A)</MenuItem>
                  <MenuItem value="createdAt">Data de Criação (Antiga-Nova)</MenuItem>
                  <MenuItem value="createdAtDesc">Data de Criação (Nova-Antiga)</MenuItem>
                </Select>
              </FormControl>



              {/* Botão do acordeão para expandir/contrair filtros avançados */}
              <div style={{ marginBottom: theme.spacing(1) }}>
                <Button
                  variant="text"
                  onClick={toggleDateFilters}
                  className={`${classes.accordionButton} ${showDateFilters ? 'expanded' : ''}`}
                  startIcon={<ExpandMoreIcon />}
                >
                  Filtros
                </Button>
              </div>

              {/* Filtros avançados - visíveis apenas quando expandido */}
              {showDateFilters && (
                <Box className={classes.dateFieldsContainer}>
                  {user.profile === 'admin' && (
                    <FormControl variant="outlined" fullWidth style={{ marginBottom: theme.spacing(2) }}>
                      <InputLabel>Filtrar por Usuário</InputLabel>
                      <Select
                        value={filterUserId}
                        onChange={handleFilterUserChange}
                        label="Filtrar por Usuário"
                      >
                        <MenuItem value={0}>
                          <em>Todos</em>
                        </MenuItem>
                        {users.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.name} {u.id === user.id ? '(você)' : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <TextField
                    label="Data Inicial"
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    style={{ marginBottom: theme.spacing(2) }}
                  />
                  <TextField
                    label="Data Final"
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    style={{ marginBottom: theme.spacing(2) }}
                  />

                  <Box className={classes.filterRow}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleApplyFilters}
                      fullWidth
                    >
                      Aplicar
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleClearFilters}
                      fullWidth
                    >
                      Limpar
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
                    ) : (
            // Layout Desktop - Original
            <>
              <Title>{i18n.t("tags.title")} ({tags.length})</Title>
              <MainHeaderButtonsWrapper>
              <Box className={classes.filtersContainer}>
                <Box className={classes.filterRow}>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
                  className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={orderBy}
                onChange={handleOrderChange}
                label="Ordenar por"
              >
                <MenuItem value="name">Nome (A-Z)</MenuItem>
                <MenuItem value="nameDesc">Nome (Z-A)</MenuItem>
                <MenuItem value="id">ID (Menor-Maior)</MenuItem>
                <MenuItem value="idDesc">ID (Maior-Menor)</MenuItem>
                <MenuItem value="user">Dono (A-Z)</MenuItem>
                <MenuItem value="userDesc">Dono (Z-A)</MenuItem>
                <MenuItem value="createdAt">Data de Criação (Antiga-Nova)</MenuItem>
                <MenuItem value="createdAtDesc">Data de Criação (Nova-Antiga)</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel>Filtrar por Usuário</InputLabel>
              <Select
                value={filterUserId}
                onChange={handleFilterUserChange}
                label="Filtrar por Usuário"
                disabled={user.profile !== 'admin'}
              >
                {user.profile === 'admin' && (
                  <MenuItem value={0}>
                    <em>Todos</em>
                  </MenuItem>
                )}
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name} {u.id === user.id ? '(você)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {user.profile === 'admin' && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenTagModal}
              >
                {i18n.t("tags.buttons.add")}
              </Button>
            )}
              </Box>
          
              <Box className={classes.filterRow}>
                <Box className={classes.dateFieldsContainer}>
            <TextField
              label="Data Inicial"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              className={classes.formControl}
            />
            <TextField
              label="Data Final"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
              className={classes.formControl}
            />
                </Box>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleApplyFilters}
              style={{ minWidth: '100px' }}
            >
              Aplicar
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearFilters}
              style={{ minWidth: '100px' }}
            >
              Limpar
            </Button>
              </Box>
            </Box>
          </MainHeaderButtonsWrapper>
            </>
        )}
      </MainHeader>


      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {isSelectionMode && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isIndeterminate()}
                    checked={isAllSelected()}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </TableCell>
              )}
              <TableCell align="center">{i18n.t("tags.table.name")}</TableCell>
              <TableCell align="center">Dono</TableCell>
              <TableCell align="center">
                {i18n.t("tags.table.contacts")}
              </TableCell>
              <TableCell align="center">Classificação</TableCell>
              <TableCell align="center">
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.map((tag) => (
              <TableRow 
                key={tag.id}
                hover
                selected={isSelectionMode && isTagSelected(tag.id)}
                style={{
                  backgroundColor: (isSelectionMode && isTagSelected(tag.id)) ? 'rgba(0, 0, 0, 0.04)' : 
                                  pressingTagId === tag.id ? 'rgba(0, 0, 0, 0.12)' : 'inherit',
                  cursor: user.profile === 'admin' ? 'pointer' : 'default',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (user.profile === 'admin' && !isSelectionMode) {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (user.profile === 'admin' && !isSelectionMode) {
                    e.currentTarget.style.backgroundColor = 'inherit';
                  }
                  // Também chama o handleMouseLeave para o long press
                  handleMouseLeave();
                }}
                onTouchStart={(e) => {
                  if (user.profile === 'admin' && !isSelectionMode) {
                    // Hover effect para mobile
                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
                    setPressingTagId(tag.id);
                    e.preventDefault();
                    startLongPress();
                  }
                }}
                onTouchEnd={(e) => {
                  if (user.profile === 'admin' && !isSelectionMode) {
                    // Remove hover effect
                    e.currentTarget.style.backgroundColor = 'inherit';
                  }
                  setPressingTagId(null);
                  cancelLongPress();
                }}
                onTouchMove={() => {
                  setPressingTagId(null);
                  cancelLongPress();
                }}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
              >
                {isSelectionMode && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isTagSelected(tag.id)}
                      onChange={() => selectTag(tag)}
                      color="primary"
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <Chip
                    variant="outlined"
                    style={{
                      backgroundColor: tag.color,
                      textShadow: "1px 1px #000",
                      color: "white",
                      height: "20px",
                      fontSize: "0.7rem",
                      padding: "0 6px"
                    }}
                    label={tag.name}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {tag?.user?.name ? (
                    <Tooltip title={`Tag criada por ${tag.user.name}${tag.isGlobal ? ' (Tag Global)' : ''}`} arrow>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: tag.userId === user.id ? 'bold' : 'normal',
                        color: tag.userId === user.id ? '#3f51b5' : 'inherit'
                      }}>
                        {tag.user.name}
                        {tag.userId === user.id && " (você)"}
                        {tag.isGlobal && (
                          <Chip
                            size="small"
                            label="Global"
                            style={{
                              marginLeft: 4,
                              height: 16,
                              fontSize: '0.6rem',
                              backgroundColor: '#4caf50',
                              color: 'white'
                            }}
                          />
                        )}
                      </span>
                    </Tooltip>
                  ) : "N/A"}
                </TableCell>
                <TableCell align="center">
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    {tag?.contacts?.length || 0}
                  </span>
                </TableCell>
                                  <TableCell align="center">
                    <Chip
                      label={tag.class || "Qualificado"}
                      size="small"
                      variant="outlined"
                      style={{
                        borderColor: 
                          tag.class === "Qualificado" ? "#4caf50" : // Verde
                          tag.class === "Desqualificado" ? "#ffc107" : // Amarelo
                          tag.class === "Negativo" ? "#f44336" : // Vermelho
                          "#4caf50", // Verde como padrão
                        color: 
                          tag.class === "Qualificado" ? "#4caf50" : // Verde
                          tag.class === "Desqualificado" ? "#ffc107" : // Amarelo
                          tag.class === "Negativo" ? "#f44336" : // Vermelho
                          "#4caf50", // Verde como padrão
                        fontWeight: "bold",
                        height: "20px",
                        fontSize: "0.7rem",
                        padding: "0 6px"
                      }}
                    />
                  </TableCell>
                <TableCell align="center">
                  <IconButton 
                    size="small" 
                    onClick={() => handleShowContacts(tag?.contacts || [], tag)}
                    title="Gerenciar contatos"
                  >
                    <PeopleIcon />
                  </IconButton>

                  <IconButton size="small" disabled=
                    {
                      tag.isGlobal &&
                      (user.profile === 'user' &&
                      tag.userId !== user.id)
                    } onClick={() => handleEditTag(tag)}>
                    <EditIcon />
                  </IconButton>

                  <IconButton
                    size="small"
                    disabled=
                    {
                      tag.isGlobal &&
                      (user.profile === 'user' &&
                        tag.userId !== user.id)
                    }
                    onClick={() => {
                      setConfirmModalOpen(true);
                      setDeletingTag(tag);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {loading && <TableRowSkeleton columns={isSelectionMode ? 6 : 5} />}
          </TableBody>
        </Table>
      </Paper>

      {/* Barra de ações flutuante */}
      {isSelectionMode && (
        <Box
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            borderRadius: '8px',
            padding: '12px 16px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            zIndex: 1000,
            minWidth: '200px'
          }}
        >
          <Typography variant="body2" style={{ color: 'white', fontWeight: '500' }}>
            {selectedTags.length > 0 ? `${selectedTags.length} tag(s) selecionada(s)` : 'Modo de seleção ativo'}
          </Typography>
          
          <IconButton
            size="small"
            onClick={handleActionMenuOpen}
            style={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            <MoreVertIcon />
          </IconButton>
          
          <IconButton
            size="small"
            onClick={disableSelectionMode}
            title="Sair do modo de seleção"
            style={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            <ClearIcon />
          </IconButton>
        </Box>
      )}

      {/* Menu de ações em lote */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem 
          onClick={handleBulkDelete}
          disabled={selectedTags.length === 0}
        >
          <ListItemIcon>
            <DeleteOutlineIcon />
          </ListItemIcon>
          <ListItemText primary="Excluir selecionadas" />
        </MenuItem>
        <MenuItem 
          onClick={handleBulkClassification}
          disabled={selectedTags.length === 0}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Alterar classificação" />
        </MenuItem>
      </Menu>

      {/* Modal de confirmação para exclusão em lote */}
      <ConfirmationModal
        title={`Excluir ${selectedTags.length} tag(s)`}
        open={bulkDeleteConfirmOpen}
        onClose={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
      >
        Tem certeza que deseja excluir {selectedTags.length} tag(s) selecionada(s)? Esta ação não pode ser desfeita.
      </ConfirmationModal>

      {/* Modal para alterar classificação */}
      <Dialog
        open={classificationDialogOpen}
        onClose={() => setClassificationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Alterar Classificação</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Selecione a nova classificação para {selectedTags.length} tag(s) selecionada(s):
          </Typography>
          <FormControl fullWidth style={{ marginTop: 16 }}>
            <InputLabel>Classificação</InputLabel>
            <Select
              value={selectedClassification}
              onChange={(e) => setSelectedClassification(e.target.value)}
              label="Classificação"
            >
              <MenuItem value="Qualificado">Qualificado</MenuItem>
              <MenuItem value="Desqualificado">Desqualificado</MenuItem>
              <MenuItem value="Negativo">Negativo</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClassificationDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleClassificationConfirm}
            color="primary"
            disabled={!selectedClassification}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

    </MainContainer>
  );
};

// Componente principal que envolve com o provider
const Tags = () => {
  return (
    <BulkSelectionProvider>
      <TagsContent />
    </BulkSelectionProvider>
  );
};

export default Tags;